import { selectors as browserMetaSelectors } from 'wanderu-ducks-browser-meta';
import Logger from 'wanderu-logger';
import wapiService from 'wanderu-service-wapi';

import { gtm, reportGtmUserData } from '../gtm';
import { SnowplowService } from '../snowplow';
import {
    isEmailDrivenSession,
    reportEmailClickIdentify,
} from './emailClickIdentify';

const logger = Logger.getLogger('UserEvents');
let _store;

// Every user event POST is marked `keepalive`. They share one fire-and-forget
// shape, so marking them uniformly is cheaper than auditing each call site for a
// navigation that might follow -- and cheap to hold: the bodies are a few hundred
// bytes, far under keepalive's 64KB budget. Only fireCheckoutEvent needs it today
// (the Canopy bridge unloads the document ~1s after the Book click); the rest are
// marked so the next call site added to a navigating path is already covered.
//
// The invariant spans packages -- wanderu-ducks-account's referredSignUp POST
// (actions/createAccount/operations.js) is the one outside this module. Grep
// events.json before assuming that is still the full list.

function initUserEvents(store) {
    _store = store;
}

function getUserLocation() {
    if (!_store) {
        logger.warn('UserEvents not inited with redux store');
        return undefined;
    }

    return browserMetaSelectors.selectUserLocation(_store.getState());
}

// Waits for the Snowplow ids. For the identify and email-signup events, neither of
// which is fired by a click that navigates.
//
// SnowplowService.getIds() returns at once when both sp cookies are already set --
// the ordinary case -- and otherwise polls for up to 30s while the collector's answer
// lands, which a first-time visitor hits.
//
// Worth waiting for, because sp_domain_id is what these events are *for*: it is the
// cookie Blueshift stitches an address onto. An identify with no sp_domain_id is
// accepted on the strength of its email and then stitches nothing -- a silent no-op,
// which is worse than a rejection.
//
// The wait is nearly always free rather than costless: over seven days of prod, 11 of
// 41,361 identify events arrived with no cookie, so getIds() returns at once for
// practically everyone. That is why capping or memoising it is not worth doing.
//
// What a reader is more likely to be surprised by is how often this fires. source
// `booking` was 9,572 identifies across 5,427 distinct cookies, about 1.76 per
// visitor: Checkout.jsx re-fires whenever the email or either name *value* changes,
// with no debounce, and those values reach Redux on each field's blur -- so filling
// email, then a first name, then correcting a typo in it is three identifies. Cheap
// individually, and none of these call sites navigates, so the wait costs latency on
// a fire-and-forget POST and nothing else.
//
// On the email_click path the wait is redundant: 0 of 7,591 arrived without a cookie,
// because reportEmailClickIdentify already runs behind onSnowplowLoad's cookie gate.
async function getMetaData() {
    const {
        domainUserId,
        networkUserId,
    } = await SnowplowService.getIds();

    return {
        userLocation: getUserLocation(),
        domainUserId,
        networkUserId,
    };
}

// Same fields, read without waiting. Used unconditionally by fireCheckoutEvent.
//
// `keepalive` only covers a request the browser has already dispatched, so an await
// in front of the dispatch is outside its protection: on the Canopy bridge's success
// path the document unloads about a second after the Book click, and a call still
// polling inside getIds() has issued no fetch at all. Shipping the ids we hold beats
// holding the event.
//
// Unconditional, not scoped to the clicks that actually navigate, and deliberately
// so. fireCheckoutEvent's only caller is TripSummary.onBookClick, which fires on
// every Book click; whether that click ends in window.location.assign is decided
// one layer *below* it, by isCanopyTreatment in the BookButton container's
// mapStateToProps (six conjuncts: assignment, deeplink, locale, WebView, and the
// two USD gates). Scoping the read would mean lifting that gate out of the
// container that owns it, or racing getIds() against a timeout and giving up the
// absolute guarantee for a probabilistic one. Neither is worth it, because what
// the non-navigating clicks give up is small:
//
// sp_network_id is not in messaging's identity `.or()` and is never forwarded to
// Blueshift, so losing it costs nothing. An absent sp_domain_id does cost the event
// -- an anonymous traveler's checkout carries no email, so it is the only identity
// field -- but the poll only ever supplied it for a visitor who clicks Book before
// the collector has set the sp cookie, which needs a direct landing on a shared
// trip-summary URL (the search -> summary flow has had it for minutes), and the
// event is then rejected loudly at the consumer rather than silently never sent.
// Same synchronous read clientApiService.getSessionMeta() already does.
function readMetaDataNow() {
    return {
        userLocation: getUserLocation(),
        domainUserId: SnowplowService.getDomainUserId(),
        networkUserId: SnowplowService.getNetworkUserId(),
    };
}

async function identifyUser(userInfo) {
    const {
        source,
        identityId,
        email,
        firstName,
        lastName,
        currency,
        locale,
    } = userInfo;

    // Synchronous, and ahead of the first await on purpose: most callers do not
    // await identifyUser, so anything the caller's own next dataLayer event
    // should see has to be in the model before this function yields.
    reportGtmUserData({ email, firstName, lastName });

    // Retracted on logout by wanderu-ducks-account's logOut operation.
    if (identityId) {
        gtm.dataLayer.push({ user_id: identityId });
    }

    try {
        const {
            userLocation,
            domainUserId,
            networkUserId,
        } = await getMetaData();

        return await wapiService.post('events.json', {
            // Keepalive per the module-level invariant above. This one does not
            // navigate today.
            keepalive: true,
            body: {
                event: 'user:action:identify',
                data: {
                    cognito_identity_id: identityId,
                    sp_domain_id: domainUserId,
                    sp_network_id: networkUserId,
                    email: email,
                    location: userLocation,
                    source,
                    first_name: firstName,
                    last_name: lastName,
                    user_locale: locale,
                    user_currency: currency,
                },
                event_timestamp: Date.now(),
            },
        });
    } catch (ex) {
        logger.error('identifyUser', ex);
        return null;
    }
}

async function trackEmailSignup({
    email,
    signupSource,
    locale,
    extra = {},
}) {
    // Email only: a signup carries no name. The clear inside the report is what
    // stops this inheriting the name from a preceding identify — the signup
    // address need not be the logged-in visitor's.
    reportGtmUserData({ email });

    try {
        // getMetaData, not getIds: the signup carries the visitor's location as well
        // as their Snowplow ids. It has to travel in the payload — the geo lookup
        // reports the visitor only when the visitor's own browser makes the request,
        // and a header carrying their IP is rewritten by the edge on the way to WAPI.
        // Same block, same source as the identify event above.
        const {
            userLocation,
            domainUserId,
            networkUserId,
        } = await getMetaData();

        return await wapiService.post('events.json', {
            // Keepalive per the module-level invariant above. This one does not
            // navigate today.
            keepalive: true,
            body: {
                event: 'user:action:email',
                data: {
                    email,
                    location: userLocation,
                    source: 'ui-react',
                    signup_source: signupSource,
                    sp_domain_id: domainUserId,
                    sp_network_id: networkUserId,
                    user_locale: locale,
                    ...extra,
                },
                event_timestamp: Date.now(),
            },
        });
    } catch (ex) {
        logger.error('trackEmailSignup', ex);
        return null;
    }
}

async function fireCheckoutEvent(info) {
    const {
        linkType,
        identityId,
        email,
        itineraryId,
        locale,
        currency,
    } = info;

    try {
        const {
            userLocation,
            domainUserId,
            networkUserId,
        } = readMetaDataNow();

        // `await`, not a bare `return`: returning the promise from inside the try
        // hands the rejection to the caller instead of the catch below, so a failed
        // POST became an unhandled rejection and never reached the logger. This is
        // called fire-and-forget from the Book click, so nothing downstream catches
        // it either -- the failure mode was silent in Datadog.
        return await wapiService.post('events.json', {
            // Fires on the Book click, and the Canopy checkout bridge ends that
            // click with window.location.assign -- a full document unload. Without
            // keepalive the browser cancels the in-flight POST and the traveler
            // never enters Blueshift's cart-abandonment journey. Same bug as
            // MAIN-8189 (commit 6f450eb17), which the bridge reintroduced; see
            // .docs/knowledge/orders/canopy-checkout-bridge.md, "What the unload
            // costs", which points back here.
            keepalive: true,
            body: {
                event: 'user:action:checkout',
                data: {
                    cognito_identity_id: identityId,
                    sp_domain_id: domainUserId,
                    sp_network_id: networkUserId,
                    email: email,
                    location: userLocation,
                    user_locale: locale,
                    user_currency: currency,
                    link_type: linkType,
                    itinerary_id: itineraryId,
                },
                event_timestamp: Date.now(),
            },
        });
    } catch (ex) {
        logger.error('fireCheckoutEvent', ex);
        return null;
    }
}

/**
 * Reports an email-driven session if this page load came from a tracked Blueshift
 * email link, so Blueshift can stitch the anonymous Snowplow cookie to the recipient.
 * Thin wiring over `reportEmailClickIdentify` — supplies the real `identifyUser`.
 *
 * Guards on the query string first: almost every page load is not email-driven and
 * should cost a single read. Returns the outcome (or null when there was nothing to
 * do) so the caller decides what deserves a log line.
 *
 * `search` must be the query string captured at page load, not read here: this fires
 * once the Snowplow tracker is ready, which can be up to 30s later for a first-time
 * visitor, and any navigation in between drops `bsft_uid` from the live URL.
 *
 * See wanderu/strategy#418 and wanderu/ui-react#1091.
 */
function identifyEmailClickSessionIfPresent({ locale, currency, search } = {}) {
    if (!isEmailDrivenSession(search)) {
        return Promise.resolve(null);
    }

    return reportEmailClickIdentify({ currency, identify: identifyUser, locale });
}

export {
    fireCheckoutEvent,
    identifyEmailClickSessionIfPresent,
    identifyUser,
    initUserEvents,
    trackEmailSignup,
};
